import { create } from 'zustand';
import { applyTheme } from '../utils/theme';

const HISTORY_LIMIT = 120;

const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
// Before the first paint, so a dark theme does not flash light on opening the app.
applyTheme(storedUser?.theme);

const useStore = create((set, get) => ({
  // Auth state
  user: storedUser,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),

  // Company state
  companies: JSON.parse(localStorage.getItem('companies') || '[]'),
  currentCompany: JSON.parse(localStorage.getItem('currentCompany') || 'null'),

  setAuth: (user, token, companies = []) => {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', token);
    localStorage.setItem('companies', JSON.stringify(companies));

    // Set current company to first company if not already set
    const storedCompany = localStorage.getItem('currentCompany');
    const currentCompany = storedCompany ? JSON.parse(storedCompany) : (companies[0] || null);
    if (currentCompany) {
      localStorage.setItem('currentCompany', JSON.stringify(currentCompany));
    }

    applyTheme(user?.theme);
    set({ user, token, isAuthenticated: true, companies, currentCompany });
  },

  /** The user's theme, for this page and the next time the app opens. Saving it to their account is separate. */
  setTheme: (theme) => {
    const user = get().user;
    if (!user) return;
    const updated = { ...user, theme };
    localStorage.setItem('user', JSON.stringify(updated));
    applyTheme(theme);
    set({ user: updated });
  },

  setCompanies: (companies) => {
    localStorage.setItem('companies', JSON.stringify(companies));
    set({ companies });
  },

  setCurrentCompany: (company) => {
    localStorage.setItem('currentCompany', JSON.stringify(company));
    set({
      currentCompany: company,
      // Clear dashboards when changing companies
      dashboards: [],
      currentDashboard: null,
      currentDashboardId: null,
      dataReady: false
    });
    localStorage.removeItem('currentDashboardId');
  },

  logout: () => {
    applyTheme('light');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('companies');
    localStorage.removeItem('currentCompany');
    localStorage.removeItem('currentDashboardId');
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      companies: [],
      currentCompany: null,
      dashboards: [],
      currentDashboard: null,
      currentDashboardId: null
    });
  },

  // Metrics state
  metrics: {},
  metricDefinitions: [],

  setMetrics: (metrics) => set({ metrics }),

  updateMetric: (metricKey, data) => {
    set((state) => {
      // Recent values per metric feed the sparklines on live widgets (session only).
      const n = Number(data?.value && typeof data.value === 'object' ? data.value.current : data?.value);
      const prev = state.metricHistory[metricKey] || [];
      const metricHistory = Number.isFinite(n)
        ? { ...state.metricHistory, [metricKey]: [...prev, { t: data.timestamp || new Date().toISOString(), v: n }].slice(-HISTORY_LIMIT) }
        : state.metricHistory;
      return { metrics: { ...state.metrics, [metricKey]: data }, metricHistory };
    });
  },

  // Sparkline history per metric key: [{ t, v }] oldest first
  metricHistory: {},
  setMetricHistory: (metricKey, points) => set((state) => ({ metricHistory: { ...state.metricHistory, [metricKey]: (points || []).slice(-HISTORY_LIMIT) } })),

  // Active alert rules (state firing) for the current company
  alerts: [],
  setAlerts: (alerts) => set({ alerts: alerts || [] }),
  applyAlertEvent: ({ event, rule }) => {
    if (!rule) return;
    set((state) => {
      const others = state.alerts.filter((a) => a.id !== rule.id);
      if (event === 'resolved' || rule.state !== 'firing') return { alerts: others };
      return { alerts: [...others, rule] };
    });
  },

  setMetricDefinitions: (definitions) => set({ metricDefinitions: definitions }),

  // Dashboard state
  dashboards: [],
  currentDashboard: null,
  currentDashboardId: localStorage.getItem('currentDashboardId'),

  setDashboards: (dashboards) => set({ dashboards }),

  setCurrentDashboard: (dashboard) => {
    if (dashboard && dashboard.id) {
      localStorage.setItem('currentDashboardId', dashboard.id);
    }
    set({ currentDashboard: dashboard, currentDashboardId: dashboard?.id });
  },

  addDashboard: (dashboard) => {
    set((state) => ({
      dashboards: [...state.dashboards, dashboard],
    }));
  },

  updateDashboard: (dashboardId, updates) => {
    set((state) => ({
      dashboards: state.dashboards.map((d) =>
        d.id === dashboardId ? { ...d, ...updates } : d
      ),
      currentDashboard:
        state.currentDashboard?.id === dashboardId
          ? { ...state.currentDashboard, ...updates }
          : state.currentDashboard,
    }));
  },

  deleteDashboard: (dashboardId) => {
    set((state) => ({
      dashboards: state.dashboards.filter((d) => d.id !== dashboardId),
      currentDashboard:
        state.currentDashboard?.id === dashboardId
          ? null
          : state.currentDashboard,
    }));
  },

  // WebSocket state
  wsConnected: false,
  setWsConnected: (connected) => set({ wsConnected: connected }),

  // Last update time per dashboard (dashboardId => timestamp)
  dashboardLastUpdates: {},
  setDashboardLastUpdate: (dashboardId, time) => {
    set((state) => ({
      dashboardLastUpdates: {
        ...state.dashboardLastUpdates,
        [dashboardId]: time,
      },
    }));
  },

  // Legacy support - keep for backwards compatibility
  lastUpdate: null,
  setLastUpdate: (time) => {
    const currentDashboardId = get().currentDashboard?.id;
    if (currentDashboardId) {
      get().setDashboardLastUpdate(currentDashboardId, time);
    }
    set({ lastUpdate: time });
  },

  // Data readiness gate - prevents widgets rendering before metrics are loaded
  dataReady: false,
  setDataReady: (ready) => set({ dataReady: ready }),

  // Monotonic counter to invalidate stale HTTP metric responses
  metricsLoadId: 0,
  incrementMetricsLoadId: () => {
    const newId = get().metricsLoadId + 1;
    set({ metricsLoadId: newId });
    return newId;
  },

  // Loading states
  isLoading: false,
  setLoading: (loading) => set({ isLoading: loading }),

  // Edit mode for dashboard
  isEditMode: false,
  setEditMode: (editMode) => set({ isEditMode: editMode }),

  // Dashboard-level date range (inherited by report widgets); null = each report keeps its own
  dashboardDateRange: null,
  setDashboardDateRange: (range) => set({ dashboardDateRange: range || null }),

  // Period a viewer picked on an individual report or statement widget
  // (widget id → range). Not saved; cleared when the dashboard changes.
  widgetPeriods: {},
  setWidgetPeriod: (widgetId, range) => set((state) => {
    const next = { ...state.widgetPeriods };
    if (range) next[widgetId] = range; else delete next[widgetId];
    return { widgetPeriods: next };
  }),
  clearWidgetPeriods: () => set({ widgetPeriods: {} }),

  // Dashboard filters the viewer has chosen (member → { values, label, sourceWidgetId }).
  // A filter set by clicking a chart remembers that widget, which keeps showing
  // everything so another value can be picked. Not saved; cleared when the dashboard changes.
  dashboardFilters: {},
  setDashboardFilter: (member, values, { label, sourceWidgetId = null } = {}) => set((state) => {
    const next = { ...state.dashboardFilters };
    if (values && values.length) next[member] = { values: values.map(String), label: label || state.dashboardFilters[member]?.label || member, sourceWidgetId };
    else delete next[member];
    return { dashboardFilters: next };
  }),
  clearDashboardFilters: () => set({ dashboardFilters: {} }),

  // Wall mode (fullscreen on a TV): compact report rendering
  wallMode: false,
  setWallMode: (on) => set({ wallMode: Boolean(on) }),

  // Dataset freshness by dataset name for the current company (fresh|overdue|never)
  datasetFreshness: {},
  setDatasetFreshness: (map) => set({ datasetFreshness: map || {} }),

  // User preferences
  animationsEnabled: localStorage.getItem('animationsEnabled') !== 'false', // default true
  setAnimationsEnabled: (enabled) => {
    localStorage.setItem('animationsEnabled', enabled);
    set({ animationsEnabled: enabled });
  },
}));

export default useStore;
